import { Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store } from '@ngrx/store';
import { UserSettingsService } from './user-settings.service';
import { AttributePresetStore } from './attribute-preset-store.base';
import { coerceCvd, resolveCvd, CvdPreset, ResolvedCvd, CVD_KEY, CVD_STORAGE_KEY } from './cvd.util';

/**
 * App-wide cache of the user's colour-vision preference, and the only writer of the `data-cvd`
 * attribute on <html>.
 *
 * The third `AttributePresetStore`, and the first with NO OS SIGNAL — see cvd.util.ts for why
 * there is nothing to follow and why the config passes `mediaQuery: null` rather than a query
 * that can never match. Everything else is the base's: the boot-stamp deference, the
 * promote-an-equal-default adopt, the persist-before-emit ordering, the cross-tab broadcast.
 *
 * WHAT IT PAINTS, AND WHAT IT DOES NOT. Stamping `data-cvd='protan'` swaps four token blocks —
 * the categorical families only; every other slot is byte-identical to the block it re-values.
 * It does not touch Angular Material's own ~903 colour declarations, and it is not a filter over
 * the page: a user who needs the whole UI transformed is better served by their OS or browser's
 * own vision tools. This preference re-values the colours this app chose, for the deficiency the
 * user names.
 */
@Injectable({ providedIn: 'root' })
export class CvdService extends AttributePresetStore<CvdPreset, ResolvedCvd> {
  constructor(
    userSettings: UserSettingsService,
    store: Store,
    zone: NgZone,
    @Inject(DOCUMENT) doc: Document,
  ) {
    super(userSettings, store, zone, doc, {
      settingsKey: CVD_KEY,
      attributeName: 'data-cvd',
      mirrorKey: CVD_STORAGE_KEY,
      // sessionStorage, and the only store in this family that asks for it. See
      // AttributePresetConfig.mirrorArea, and identityScoped() below for the disclosure it is
      // the other half of: clearing on logout and on a switch covers every way an identity is
      // REPLACED, and none of the ways a shift simply ENDS.
      mirrorArea: 'session',
      mediaQuery: null,
    }, 'none');
  }

  protected coerce(v: unknown): CvdPreset {
    return coerceCvd(v);
  }

  /** `mediaMatches` is ignored and has to be: there is no media query behind it. Declared to
   *  satisfy the base's contract rather than because this preset has two readings. */
  protected resolve(preset: CvdPreset): ResolvedCvd {
    return resolveCvd(preset);
  }

  /**
   * The boot script's whitelist, restated. Only a LIVE deficiency resolves; anything else — a
   * value from a newer build, a hand-edited mirror, junk — answers `null`, which is the same
   * answer the boot script's own `indexOf` check gives it. `undefined` is never returned: unlike
   * the contrast mirror there is no third state to distinguish, because with no OS signal to
   * consult "pinned off" and "nothing said" are one instruction.
   */
  protected resolvedFromMirror(mirror: string): ResolvedCvd {
    return resolveCvd(coerceCvd(mirror));
  }

  /**
   * THE ONE PLACE THIS PRESET PARTS COMPANY WITH THE THEME: it is cleared the moment the identity
   * that chose it goes away — a logout, OR a cross-tab switch to another user.
   *
   * `AttributePresetStore.identityScoped()` answers false by default, and the theme deliberately keeps
   * its mirror — a colour scheme is not an identity leak, and dropping it would reintroduce the cold-
   * load flash the mirror exists to prevent. That reasoning does not carry here, for the reason
   * cvd.util.ts gives for there being no 'system' value at all: which deficiency a user has is a
   * MEDICAL FACT, and the two things an identity change would otherwise leave behind both
   * disclose it to whoever sits down next. `data-cvd` stays stamped, so the login page and every
   * pre-/app screen keep painting the outgoing user's palette; and `g15.cvd` stays in web
   * storage — plaintext, readable by any script on the origin, re-stamped on the next cold load
   * by whoever opens the app. On the shared counter and warehouse machines this app is used on,
   * that is the previous operator's condition on display to the next one. AvatarService clears
   * its own state on the same two events one field over, for the same shape of reason: personal,
   * visible state that must not outlive its owner.
   *
   * THIS FLAG COVERS EVERY WAY AN IDENTITY IS REPLACED AND NONE OF THE WAYS A SHIFT SIMPLY ENDS,
   * which is why it is not the whole answer: closing the tab, or quitting the browser, fires
   * neither a logout nor a switch, and on a shared counter machine that is the ordinary way the
   * app is left. The other half is `mirrorArea: 'session'` in the config above — the mirror dies
   * with the tab, so there is nothing for the boot script to re-stamp for the next operator.
   * Read the two together: this one stops the outgoing palette painting on the login page, that
   * one stops it coming back tomorrow.
   *
   * WHY THE FLAG AND NOT AN `onLoggedOut` OVERRIDE: the switch is the case that matters
   * most and is the one a logout hook cannot see. A cross-tab A -> B hand-off keeps the shared
   * refresh cookie, so isAuthenticated never drops, `userId` merely changes value, and the tab is
   * navigated to /app under B — with A's palette still painted and A's mirror still on disk until
   * B's settings GET resolves, or for good if it errors. That is the shared-counter disclosure
   * above, on the path this app documents as the common one.
   *
   * IT IS A FLAG RATHER THAN A HOOK because it buys a second guarantee for free: the same
   * predicate makes the base REFUSE an inbound cross-tab broadcast while no identity is in place.
   * Without it the clear below is undone by the next message on the shared channel — persist()
   * broadcasts only when its write confirms, which is asynchronous to a teardown — and A's
   * palette is re-stamped, and A's mirror re-written, on the login page. Clearing and refusing to
   * re-adopt are one claim; splitting them across two overrides is how half of it goes missing.
   *
   * THE ACCEPTED COST, so nobody "fixes" it back by symmetry with ThemeService: a returning
   * protanope gets the ordinary palette from first paint until their settings load resolves —
   * exactly the flash the mirror was built to remove. That is the trade taken, privacy over the
   * flash, and it is a decision rather than an oversight. Reversing it means arguing that the
   * disclosure is acceptable, not that the flash is annoying.
   */
  protected override identityScoped(): boolean {
    return true;
  }
}
